import React, { useEffect, useState } from "react";
import TaskTable from "../../components/programStaff/TaskTable";
import { getDashboardData } from "../../api/programStaffApi";
import Topbar from "../../components/TopbarNav";
import MasterSideBar from "../../components/MasterSideBar"; 
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumStatCard from "../../components/common/PremiumStatCard";
import PremiumCard from "../../components/common/PremiumCard";
import { 
  Plus, CheckCircle, Clock, 
  AlertTriangle, Calendar, Layers,
  X 
} from "lucide-react";
import LeaveRequestForm from "../../components/LeaveRequestForm";
import Spinner from "../../components/programStaff/Spinner";
import { jwtDecode } from "jwt-decode";
import MyTaskCalendar from "../../components/programStaff/MyTaskCalendar";
import { motion, AnimatePresence } from "framer-motion";

const StaffDashboards = () => {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showLeaveForm, setShowLeaveForm] = useState(false);

  const token = localStorage.getItem("token");
  const decoded = token ? jwtDecode(token) : null;
  const staffId = decoded ? decoded.id : null;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await getDashboardData();
        if (data) {
          setUser(data.user);
          const now = new Date();
          const overdueTasks = data.tasks?.filter((task) => {
            const endDate = task.end ? new Date(task.end) : null;
            const normalizedStatus = task.status ? task.status.trim().toLowerCase() : "";
            return endDate && endDate < now && normalizedStatus !== "completed";
          }).length;

          setStats({ ...data.stats, overdueTasks });
          setTasks(data.tasks);
        }
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
      <MasterSideBar 
        visible={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
        <Topbar onSidebarToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
          <div className="max-w-7xl mx-auto">
            
            <div className="flex flex-col items-center text-center mb-12">
              <PremiumHeader 
                title={`Hi, ${user?.firstName || 'Staff'}`} 
                lastWordAccent="Overview" 
                subtitle="Track your assigned tasks, project progress, and leave status in one place." 
              />
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowLeaveForm(true)}
                className="mt-8 flex items-center gap-3 bg-gray-900 text-white px-10 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-orange-600 shadow-2xl shadow-gray-200 transition-all active:scale-95"
              >
                <Plus size={16} strokeWidth={3} /> Submit Absence Request
              </motion.button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {[
                { title: "Total Tasks", val: stats?.totalTasks || "0", icon: Layers, accent: "blue", desc: "Assigned lifecycle" },
                { title: "In Progress", val: stats?.inProgressTasks || "0", icon: Clock, accent: "orange", desc: "Active focus" },
                { title: "Completed", val: stats?.completedTasks || "0", icon: CheckCircle, accent: "emerald", desc: "Finalized goals" },
                { title: "Overdue", val: stats?.overdueTasks || "0", icon: AlertTriangle, accent: "red", desc: "Immediate action" }
              ].map((stat, i) => (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  key={i}
                >
                  <PremiumStatCard 
                    title={stat.title} 
                    value={stat.val} 
                    icon={<stat.icon className={`text-${stat.accent}-600`} />} 
                    iconBg={`bg-${stat.accent}-50`}
                    description={stat.desc}
                  />
                </motion.div>
              ))}
            </div>

            {/* Calendar Section */}
            <div className="mb-12">
              <PremiumCard title="Task Calendar" icon={<Calendar size={18} />}>
                <div className="pt-2">
                  <MyTaskCalendar />
                </div>
              </PremiumCard>
            </div>

            {/* Task Table Section */}
            <div className="mb-12">
              <PremiumCard title="Assigned Tasks" icon={<Layers size={18} />}>
                {tasks && tasks.length > 0 ? (
                  <TaskTable tasks={tasks} />
                ) : (
                  <div className="text-center py-24 bg-gray-50/50 rounded-3xl border-2 border-dashed border-gray-100 mx-4 my-4">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">No active tasks assigned to your queue.</p>
                  </div>
                )}
              </PremiumCard>
            </div>
          </div>
        </main>
      </div>

      {/* Modal Overlay for Leave Request */}
      <AnimatePresence>
        {showLeaveForm && staffId && token && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              onClick={() => setShowLeaveForm(false)} 
              className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
            />
            <motion.div 
              initial={{ scale: 0.95, opacity: 0, y: 20 }} 
              animate={{ scale: 1, opacity: 1, y: 0 }} 
              exit={{ scale: 0.95, opacity: 0, y: 20 }} 
              className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-gray-100"
            >
              <div className="px-10 py-8 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center gap-4">
                  <div className="size-10 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center">
                    <Plus size={20} strokeWidth={3} />
                  </div>
                  <div>
                    <h2 className="text-xs font-black text-gray-800 uppercase tracking-widest">Leave Application</h2>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight mt-1">Submit your request for review</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowLeaveForm(false)} 
                  className="p-3 bg-white hover:bg-gray-100 rounded-2xl transition-all active:scale-95 border border-gray-100 shadow-sm"
                >
                  <X size={20} className="text-gray-400" />
                </button>
              </div>
              <div className="p-10 overflow-y-auto max-h-[calc(90vh-120px)]">
                <LeaveRequestForm staffId={staffId} token={token} onClose={() => setShowLeaveForm(false)} />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StaffDashboards;